import { KLineData } from 'klinecharts';
import { klineLineStyle, lineStyleToKLine } from './lineStyle';

interface AdvancedMAIndicator {
  name: string;
  shortName: string;
  calcParams: any[];
  figures: Array<{
    key: string;
    title: string;
    type: string;
  }>;
  regenerateFigures?: (params: any[]) => Array<{
    key: string;
    title: string;
    type: string;
  }>;
  calc: (
    dataList: KLineData[],
    indicator: {
      calcParams: any[];
      figures: any[];
      extendData?: any;
    }
  ) => any[];
  styles?: any; // Dodajemy styles
}

// Helper function to get price value based on "Apply to" setting
const getPriceValue = (kLineData: KLineData, applyTo: string): number => {
  switch (applyTo) {
    case 'Open':
      return kLineData.open;
    case 'High':
      return kLineData.high;
    case 'Low':
      return kLineData.low;
    case 'Close':
      return kLineData.close;
    case 'Median Price (HL/2)':
      return (kLineData.high + kLineData.low) / 2;
    case 'Typical Price (HLC/3)':
      return (kLineData.high + kLineData.low + kLineData.close) / 3;
    case 'Weighted Close (HLCC/4)':
      return (kLineData.high + kLineData.low + kLineData.close * 2) / 4;
    default:
      return kLineData.close;
  }
};

export const createAdvancedMA = (settings: any): AdvancedMAIndicator => {
  const values = settings?.values ?? settings ?? {};
  const period = values.Period || 14;
  const shift = values.Shift || 0;
  const method = values.Method || 'Simple';
  const applyTo = values['Apply to'] || 'Close';
  const maStyle =
    settings?.styles?.ma ??
    settings?.styleSettings?.ma ??
    {};
  const color = maStyle.color || settings?.color || '#FF9600';
  const lineStyle = lineStyleToKLine(maStyle.style);
  
  return {
    name: 'ma', // Changed from 'ADVANCED_MA' to match indicatorId
    shortName: `MA${period}`,
    calcParams: [period, shift, method, applyTo],
    figures: [
      {
        key: 'ma',
        title: `MA${period}: `,
        type: 'line',
      },
    ],
    styles: {
      lines: [klineLineStyle(color, 2, lineStyle)],
    },
    regenerateFigures: (params: any[]) => {
      const [period] = params;
      return [
        {
          key: 'ma',
          title: `MA${period}: `,
          type: 'line',
        },
      ];
    },
    /**
     * Single-pass O(N) moving averages.
     *
     * Simple MA uses a sliding window sum (no per-bar slice/reduce).
     * Exponential / Smoothed update from the previous value.
     * Linear Weighted uses a ring buffer to keep the last `period` values without
     * re-allocating an array every bar.
     */
    calc: (dataList, indicator) => {
      const { calcParams = [period, shift, method, applyTo] } = indicator;
      const [currentPeriod, currentShift, currentMethod, currentApplyTo] =
        calcParams as [number, number, string, string];
      const n = dataList.length;
      const p = currentPeriod;
      const results: Array<{ ma?: number }> = new Array(n);

      let smaSum = 0;
      let prevEma = 0;
      let prevSmoothed = 0;
      const lwBuffer: number[] = currentMethod === "Linear Weighted" ? new Array(p) : [];
      let lwIndex = 0;
      let lwCount = 0;
      const expMultiplier = 2 / (p + 1);
      const smoothedAlpha = 1 / p;
      const lwWeightSum = (p * (p + 1)) / 2;

      let priceHistoryForLW: number[] | null = null;
      if (currentMethod === "Linear Weighted") {
        priceHistoryForLW = lwBuffer;
      }

      for (let i = 0; i < n; i++) {
        const priceValue = getPriceValue(dataList[i], currentApplyTo);

        if (currentMethod === "Simple") {
          smaSum += priceValue;
          if (i >= p) {
            smaSum -= getPriceValue(dataList[i - p], currentApplyTo);
          }
          if (i >= p - 1) {
            results[i] = { ma: smaSum / p };
          } else {
            results[i] = {};
          }
        } else if (currentMethod === "Exponential") {
          if (i === 0) {
            prevEma = priceValue;
          } else {
            prevEma = (priceValue - prevEma) * expMultiplier + prevEma;
          }
          if (i >= p - 1) {
            results[i] = { ma: prevEma };
          } else {
            results[i] = {};
          }
        } else if (currentMethod === "Smoothed") {
          if (i === 0) {
            prevSmoothed = priceValue;
          } else {
            prevSmoothed =
              smoothedAlpha * priceValue + (1 - smoothedAlpha) * prevSmoothed;
          }
          if (i >= p - 1) {
            results[i] = { ma: prevSmoothed };
          } else {
            results[i] = {};
          }
        } else if (currentMethod === "Linear Weighted" && priceHistoryForLW) {
          priceHistoryForLW[lwIndex] = priceValue;
          lwIndex = (lwIndex + 1) % p;
          if (lwCount < p) lwCount += 1;

          if (lwCount === p) {
            let weightedSum = 0;
            for (let k = 0; k < p; k++) {
              // index 0 is oldest; lwIndex points to the slot that holds the oldest after write
              const sample = priceHistoryForLW[(lwIndex + k) % p];
              weightedSum += sample * (k + 1);
            }
            results[i] = { ma: weightedSum / lwWeightSum };
          } else {
            results[i] = {};
          }
        } else {
          smaSum += priceValue;
          if (i >= p) {
            smaSum -= getPriceValue(dataList[i - p], currentApplyTo);
          }
          if (i >= p - 1) {
            results[i] = { ma: smaSum / p };
          } else {
            results[i] = {};
          }
        }
      }

      if (currentShift !== 0) {
        const shifted: Array<{ ma?: number }> = new Array(n);
        for (let i = 0; i < n; i++) {
          shifted[i] = {};
        }
        for (let i = 0; i < n; i++) {
          const target = i + currentShift;
          if (target >= 0 && target < n) {
            shifted[target] = results[i];
          }
        }
        return shifted;
      }

      return results;
    },
  };
};